/**
 * @fileoverview My Page — the signed-in Sales user's own targets.
 * @description Reads the backend's My Page contract (Confluence 3.5.11.15-.17): the KPI counts
 * (GET /sales/targets/summary) and the target table (GET /sales/targets). The backend does the
 * scoping — a Super Admin sees every target, anyone else only the targets assigned to them — and
 * the filtering and sorting, so this page never resolves a hierarchy or the viewer's own id. Each
 * target counts once under its derived display status (On Track, At Risk, Overdue, Awaiting
 * Payment, Completed, Closed) in the current April to March financial year; Closed targets (Lost
 * or Rejected) stay in the table, greyed out, unless hidden, and Completed ones are not listed.
 * Clicking a row opens the target (MyPageTargetDetailModal), whose footer offers one button for
 * each action the backend allows the viewer on it (`allowedActions`): update stage, reopen,
 * reassign or request a reassignment, send payment details and confirm payment. Each opens its
 * dialog (MyPageActionHost) and, once saved, the target shows its new state. The header's
 * Organisation Details button opens the record Send Payment Details emails to a bank, editable by a
 * Super Admin. The table offers search, stage, status, priority and whether to include closed
 * targets.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  DEFAULT_MY_PAGE_FILTERS,
  hasActiveMyPageFilters,
  isResultCapExceeded,
  MyPageActionHost,
  MyPageSummaryPanel,
  MyPageTableCard,
  MyPageTargetDetailModal,
  OrganisationDetailsModal,
  PageLoadError,
  TableSkeleton,
  toMyPageTargetFilters,
  useMyPageRowAction,
  useMyPageSummary,
  useMyPageTargets,
} from '@/features/sales'
import Button from '@/shared/components/atoms/Button'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { ORGANISATION_UPDATE_PRIVILEGE } from '@/shared/constants/organisation-privileges.constants'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import type {
  MyPageActionRequest,
  MyPageFilterState,
  MyPageTargetAction,
  MyPageTargetRow,
} from '@/features/sales'

const EMPTY_ROWS: MyPageTargetRow[] = []
const COLUMN_COUNT = 7

/**
 * @description Reads the summary and the target table for the filters. A list too long to fetch
 * (more than 5,000 targets) is not a page-level failure: the table stays, empty, with a notice to
 * narrow the filters, so the user can still change them.
 * @param {MyPageFilterState} filters - The table's filters.
 * @returns {{ summary: object | undefined; rows: MyPageTargetRow[]; isPending: boolean;
 * isError: boolean; tooManyResults: boolean }} What the page shows and in which state.
 */
function useMyWorkData(filters: MyPageFilterState) {
  const search = useDebouncedValue(filters.query.trim())
  const summaryQuery = useMyPageSummary()
  const targetsQuery = useMyPageTargets(toMyPageTargetFilters(filters, search))
  const tooManyResults = isResultCapExceeded(targetsQuery.error)

  return {
    summary: summaryQuery.data,
    rows: targetsQuery.data ?? EMPTY_ROWS,
    isPending: summaryQuery.isPending || targetsQuery.isPending,
    isError: summaryQuery.isError || (targetsQuery.isError && !tooManyResults),
    tooManyResults,
  }
}

/**
 * @description Whether the signed-in user may edit the organisation's bank details.
 * @returns {boolean} True when the session holds the organisation-update privilege.
 */
function useCanEditOrganisation(): boolean {
  const privileges = useCurrentSession().data?.privileges ?? []
  return privileges.includes(ORGANISATION_UPDATE_PRIVILEGE)
}

/**
 * @component
 * @param {{ onClick: () => void }} props - The click handler.
 * @param {() => void} props.onClick - Called when the button is clicked.
 */
function OrganisationDetailsButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="w-fit">
      <Button
        type="button"
        size="md"
        variant="secondary"
        onClick={onClick}
        data-testid="my-work-organisation-button"
      >
        {t('sales.myWork.organisation.button')}
      </Button>
    </div>
  )
}

/**
 * @component
 */
function MyWorkPage() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<MyPageFilterState>(DEFAULT_MY_PAGE_FILTERS)
  const [detailTargetId, setDetailTargetId] = useState<string | null>(null)
  const [actionRequest, setActionRequest] = useState<MyPageActionRequest | null>(null)
  const [organisationOpen, setOrganisationOpen] = useState(false)
  const canEditOrganisation = useCanEditOrganisation()

  const { summary, rows, isPending, isError, tooManyResults } = useMyWorkData(filters)
  const anyFilter = hasActiveMyPageFilters(filters)
  const requestRowAction = useMyPageRowAction()

  /**
   * @description Resets every table filter back to its default.
   */
  const clearFilters = () => setFilters(DEFAULT_MY_PAGE_FILTERS)

  /**
   * @description Opens a row-menu action's dialog once the target has been read; if the target can't
   * be read, opens its details instead, which show the problem and a way to retry.
   * @param {MyPageTargetAction} action - The action chosen in the row's menu.
   * @param {string} targetId - The target it applies to.
   */
  const openRowAction = (action: MyPageTargetAction, targetId: string) => {
    void requestRowAction(action, targetId).then((request) => {
      if (request) setActionRequest(request)
      else setDetailTargetId(targetId)
    })
  }

  const organisationModal = organisationOpen ? (
    <OrganisationDetailsModal
      canEdit={canEditOrganisation}
      onClose={() => setOrganisationOpen(false)}
    />
  ) : null

  const header = <PageHeader title={t('nav.myPage')} description={t('sales.myWork.description')} />

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <KpiStripSkeleton />
        <TableSkeleton columns={COLUMN_COUNT} />
        {organisationModal}
      </div>
    )
  }

  if (isError || !summary) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <PageLoadError message={t('sales.myWork.loadError')} testId="my-work-load-error" />
        {organisationModal}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {header}

      <MyPageSummaryPanel summary={summary} />

      <MyPageTableCard
        financialYear={summary.financialYear}
        headerAction={<OrganisationDetailsButton onClick={() => setOrganisationOpen(true)} />}
        filters={filters}
        filtersActive={anyFilter}
        tooManyResults={tooManyResults}
        rows={rows}
        onFilterChange={(patch) => setFilters((current) => ({ ...current, ...patch }))}
        onClear={clearFilters}
        onOpen={setDetailTargetId}
        onAction={openRowAction}
      />

      <MyPageTargetDetailModal
        targetId={actionRequest ? null : detailTargetId}
        onClose={() => setDetailTargetId(null)}
        onAction={(action, target) => setActionRequest({ action, target })}
      />
      <MyPageActionHost request={actionRequest} onClose={() => setActionRequest(null)} />
      {organisationModal}
    </div>
  )
}

export default MyWorkPage
